import React, { useState, useEffect, type CSSProperties, type ChangeEvent, type FocusEvent } from 'react';

/**
 * ValorInputControlled - Input de valor monetário com formatação brasileira
 *
 * Durante a edição mostra valor simples (1234,56)
 * Fora da edição mostra formatado com milhares (1.234,56)
 *
 * @param {Object} props
 * @param {number|string} props.value - Valor atual (número ou string)
 * @param {Function} props.onChange - Callback (numericValue) => void
 * @param {Object} props.style - Estilos do input
 * @param {boolean} props.hasError - Se tem erro de validação
 * @param {boolean} props.disabled - Se está desabilitado
 * @param {string} props.placeholder - Placeholder
 * @param {boolean} props.showPrefix - Mostrar prefixo R$ dentro do input (default: false)
 * @param {boolean} props.showLabelSuffix - Mostrar sufixo (R$) no label (default: true)
 * @param {string} props.label - Label do campo (opcional)
 */

/**
 * Tipo aceito pelo valor: o runtime coage `value` via `String(val || 0)`,
 * `parseFloat(...)`, e trata `typeof val === 'number'`. Portanto aceitamos
 * number, string, null ou undefined — exatamente o que os 9 consumidores usam.
 */
export type ValorInputControlledValue = number | string | null | undefined;

export interface ValorInputControlledProps {
  value: ValorInputControlledValue;
  onChange?: (numericValue: number) => void;
  /** Blank blur stores nothing. Cost fields keep the default, which stores 0. */
  emptyAsNull?: boolean;
  onEmpty?: () => void;
  style?: CSSProperties;
  hasError?: boolean;
  disabled?: boolean;
  placeholder?: string;
  showPrefix?: boolean;
  showLabelSuffix?: boolean;
  label?: string;
}

export default function ValorInputControlled({
  value,
  onChange,
  emptyAsNull = false,
  onEmpty,
  style = {},
  hasError = false,
  disabled = false,
  placeholder = '0,00',
  showPrefix = false,
  showLabelSuffix = true,
  label = ''
}: ValorInputControlledProps) {
  const [localValue, setLocalValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Formatar valor com separador de milhares (1.234,56)
  const formatValueWithThousands = (val: ValorInputControlledValue): string => {
    if (typeof val === 'number') {
      return val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    const strValue = String(val || 0);
    if (strValue.includes(',')) {
      const cleaned = strValue.replace(/\./g, '').replace(',', '.');
      const num = parseFloat(cleaned) || 0;
      return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    const num = parseFloat(strValue) || 0;
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Converter string formatada para número
  const parseFormattedValue = (formattedValue: ValorInputControlledValue): number => {
    if (!formattedValue && formattedValue !== 0) return 0;
    if (typeof formattedValue === 'number') return formattedValue;
    const strValue = String(formattedValue);
    if (strValue.includes(',')) {
      const cleaned = strValue.replace(/\./g, '').replace(',', '.');
      return parseFloat(cleaned) || 0;
    }
    return parseFloat(strValue) || 0;
  };

  // Quando value externo muda e não estamos editando, atualiza o display
  useEffect(() => {
    if (!isEditing) {
      if (emptyAsNull && (value == null || value === '')) {
        setLocalValue('');
        return;
      }
      setLocalValue(formatValueWithThousands(value || 0));
    }
  }, [value, isEditing, emptyAsNull]);

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    setIsEditing(true);
    if (emptyAsNull && (value == null || value === '')) {
      setLocalValue('');
      return;
    }
    const numValue = parseFormattedValue(value);
    // Formato brasileiro simples (só com vírgula decimal)
    setLocalValue(numValue.toFixed(2).replace('.', ','));
    // Seleciona todo o texto
    setTimeout(() => e.target.select(), 0);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    // Permite apenas números, vírgula e ponto
    const rawValue = e.target.value.replace(/[^\d.,]/g, '');
    setLocalValue(rawValue);
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (emptyAsNull && localValue.trim() === '') {
      setLocalValue('');
      onEmpty?.();
      return;
    }
    const numValue = parseFormattedValue(localValue);
    setLocalValue(formatValueWithThousands(numValue));
    if (onChange) onChange(numValue);
  };

  const inputStyle: CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    border: `1px solid ${hasError ? 'var(--color-danger)' : 'var(--panel-border)'}`,
    borderRadius: '6px',
    fontSize: '13px',
    background: disabled ? '#f9fafb' : 'white',
    textAlign: 'right',
    outline: 'none',
    transition: 'var(--transition)',
    ...style,
    ...(showPrefix ? { paddingLeft: '32px' } : {})
  };

  // Renderiza o label se fornecido
  const renderLabel = () => {
    if (!label) return null;
    const labelText = showLabelSuffix ? `${label} (R$)` : label;
    return (
      <label style={{
        display: 'block',
        marginBottom: '4px',
        fontSize: '12px',
        fontWeight: 500,
        color: 'var(--muted)'
      }}>
        {labelText}
      </label>
    );
  };

  // Renderiza o input
  const renderInput = () => {
    if (showPrefix) {
      return (
        <div style={{ position: 'relative' }}>
          <span style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--muted)',
            fontSize: '13px',
            pointerEvents: 'none'
          }}>
            R$
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={localValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            placeholder={placeholder}
            style={inputStyle}
          />
        </div>
      );
    }

    return (
      <input
        type="text"
        inputMode="decimal"
        value={localValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder={placeholder}
        style={inputStyle}
      />
    );
  };

  // Se tem label, renderiza com wrapper
  if (label) {
    return (
      <div>
        {renderLabel()}
        {renderInput()}
      </div>
    );
  }

  return renderInput();
}

/**
 * Funções auxiliares exportadas para uso em outros componentes
 */
export const formatValueWithThousands = (value: ValorInputControlledValue): string => {
  if (typeof value === 'number') {
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  const strValue = String(value || 0);
  if (strValue.includes(',')) {
    const cleaned = strValue.replace(/\./g, '').replace(',', '.');
    const num = parseFloat(cleaned) || 0;
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  const num = parseFloat(strValue) || 0;
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const parseFormattedValue = (formattedValue: ValorInputControlledValue): number => {
  if (!formattedValue && formattedValue !== 0) return 0;
  if (typeof formattedValue === 'number') return formattedValue;
  const strValue = String(formattedValue);
  if (strValue.includes(',')) {
    const cleaned = strValue.replace(/\./g, '').replace(',', '.');
    return parseFloat(cleaned) || 0;
  }
  return parseFloat(strValue) || 0;
};

/**
 * Exemplo de uso:
 *
 * import ValorInputControlled, { formatValueWithThousands, parseFormattedValue } from '@/components/ui/ValorInputControlled';
 *
 * // Com label e sufixo (R$) - padrão
 * <ValorInputControlled
 *   label="Valor"
 *   value={formData.valor}
 *   onChange={(numValue) => setFormData(prev => ({ ...prev, valor: numValue }))}
 *   hasError={!!errors.valor}
 * />
 * // Renderiza: "Valor (R$)" como label
 *
 * // Com label sem sufixo (R$)
 * <ValorInputControlled
 *   label="Desconto"
 *   showLabelSuffix={false}
 *   value={formData.desconto}
 *   onChange={(numValue) => setFormData(prev => ({ ...prev, desconto: numValue }))}
 * />
 * // Renderiza: "Desconto" como label (sem R$)
 *
 * // Com prefixo R$ dentro do input
 * <ValorInputControlled
 *   value={formData.valor}
 *   onChange={(numValue) => setFormData(prev => ({ ...prev, valor: numValue }))}
 *   showPrefix
 * />
 */
